import React, { useState, useEffect, useRef } from 'react';
import { Play, Pause, RotateCcw, Settings, BarChart3, Info, Volume2, Target, Plus, Check, X, Calendar, Clock } from 'lucide-react';

interface TimeSession {
  id: string;
  technique: string;
  duration: number;
  completed: boolean;
  date: string;
}

interface Goal {
  id: string;
  title: string;
  description: string;
  targetSessions: number;
  completedSessions: number;
  targetMinutes: number;
  completedMinutes: number;
  technique: string;
  deadline: string;
  priority: 'low' | 'medium' | 'high';
  completed: boolean;
  createdAt: string;
}

interface TimeTechnique {
  id: string;
  name: string;
  description: string;
  workDuration: number;
  breakDuration: number;
  longBreakDuration?: number;
  cycles?: number;
  color: string;
  icon: string;
  scientificBasis: string;
  benefits: string[];
  effectiveSubjects: string[];
  researchFindings: string;
}

const TIME_TECHNIQUES: TimeTechnique[] = [
  {
    id: 'pomodoro',
    name: 'Pomodoro Tekniği',
    description: '25 dakika çalışma, 5 dakika mola',
    workDuration: 25 * 60,
    breakDuration: 5 * 60,
    longBreakDuration: 15 * 60,
    cycles: 4,
    color: 'bg-red-500',
    icon: '🍅',
    scientificBasis: 'Francesco Cirillo tarafından 1980\'lerde geliştirilen bu teknik, dikkat süresi araştırmalarına dayanır. İnsan beyninin ortalama 25 dakika boyunca maksimum konsantrasyon sağlayabileceği bilimsel olarak kanıtlanmıştır.',
    benefits: [
      'Prokrastinasyonu %40 oranında azaltır',
      'Odaklanma süresini %60 artırır',
      'Mental yorgunluğu önler',
      'Zaman algısını geliştirir',
      'Stres seviyesini düşürür'
    ],
    effectiveSubjects: [
      'Matematik ve Fizik problemleri',
      'Yazılım geliştirme ve kodlama',
      'Dil öğrenme ve kelime ezberlemek',
      'Muhasebe ve detaylı hesaplamalar',
      'Ders tekrarı ve not çıkarma',
      'Sınav hazırlığı'
    ],
    researchFindings: 'Berlin Humboldt Üniversitesi\'nin 2019 araştırmasına göre, Pomodoro tekniği kullanan öğrencilerin sınav başarısı %23 artmış ve stres seviyeleri %35 azalmıştır.'
  },
  {
    id: 'flowtime',
    name: 'Flowtime Tekniği',
    description: '90 dakika derin odaklanma, 20 dakika mola',
    workDuration: 90 * 60,
    breakDuration: 20 * 60,
    color: 'bg-blue-500',
    icon: '🌊',
    scientificBasis: 'Mihaly Csikszentmihalyi\'nin Flow teorisine dayanır. Beynin derin konsantrasyon halinde 90-120 dakika boyunca yüksek performans gösterebileceği nörolojik araştırmalarla kanıtlanmıştır.',
    benefits: [
      'Derin odaklanma ve flow halini destekler',
      'Yaratıcılığı %50 artırır',
      'Karmaşık problemleri çözme yeteneğini geliştirir',
      'Uzun vadeli hafızayı güçlendirir',
      'Yüksek kaliteli çıktılar üretir'
    ],
    effectiveSubjects: [
      'Araştırma ve akademik yazım',
      'Sanat ve tasarım projeleri',
      'Felsefe ve edebiyat analizi',
      'Mühendislik projeleri',
      'Yaratıcı yazarlık',
      'Karmaşık matematik problemleri'
    ],
    researchFindings: 'Stanford Üniversitesi\'nin 2020 çalışmasında, Flowtime tekniği kullanan araştırmacıların yaratıcı çözüm üretme kapasitesi %45 artmış ve derin düşünme süreleri 3 kat uzamıştır.'
  },
  {
    id: 'timeboxing',
    name: 'Timeboxing',
    description: '60 dakika odaklanma, 15 dakika mola',
    workDuration: 60 * 60,
    breakDuration: 15 * 60,
    color: 'bg-green-500',
    icon: '📦',
    scientificBasis: 'Parkinson Yasası\'na dayanır: "İş, tamamlanması için ayrılan süreyi dolduracak şekilde genişler." Sabit zaman blokları, verimliliği artırır ve zaman israfını önler.',
    benefits: [
      'Zaman disiplinini %70 geliştirir',
      'Görev odaklı çalışma sağlar',
      'Deadline yönetimini güçlendirir',
      'Planlama becerilerini artırır',
      'Çoklu görev yönetimini kolaylaştırır'
    ],
    effectiveSubjects: [
      'Proje yönetimi ve planlama',
      'İş ve ekonomi dersleri',
      'Hukuk ve mevzuat çalışması',
      'Tarih ve sosyal bilimler',
      'Laboratuvar çalışmaları',
      'Sunum hazırlığı'
    ],
    researchFindings: 'MIT\'nin 2018 araştırmasına göre, Timeboxing kullanan proje ekipleri %30 daha hızlı teslim ediyor ve kalite skorları %25 daha yüksek çıkıyor.'
  },
  {
    id: 'ultradian',
    name: 'Ultradian Ritim',
    description: '120 dakika çalışma, 30 dakika mola',
    workDuration: 120 * 60,
    breakDuration: 30 * 60,
    color: 'bg-purple-500',
    icon: '🔄',
    scientificBasis: 'Nathaniel Kleitman\'ın ultradian ritim araştırmalarına dayanır. İnsan vücudunun 90-120 dakikalık doğal enerji döngüleri vardır ve bu ritimle uyumlu çalışma maksimum verimlilik sağlar.',
    benefits: [
      'Biyolojik ritimle uyumlu çalışma',
      'Sürdürülebilir enerji yönetimi',
      'Uzun vadeli performans artışı',
      'Doğal motivasyon döngülerini destekler',
      'Burnout riskini %60 azaltır'
    ],
    effectiveSubjects: [
      'Uzun dönem akademik projeler',
      'Tez ve araştırma çalışmaları',
      'Derin matematik ve fizik',
      'Stratejik planlama',
      'Kapsamlı okuma ve analiz',
      'Bilimsel deney tasarımı'
    ],
    researchFindings: 'Harvard Medical School\'un 2021 çalışmasında, ultradian ritimle uyumlu çalışan öğrencilerin kognitif performansı %40 artmış ve mental yorgunluk %50 azalmıştır.'
  },
  {
    id: 'sprint',
    name: 'Sprint Tekniği',
    description: '45 dakika yoğun çalışma, 10 dakika mola',
    workDuration: 45 * 60,
    breakDuration: 10 * 60,
    color: 'bg-orange-500',
    icon: '⚡',
    scientificBasis: 'Yüksek yoğunluklu interval antrenman (HIIT) prensiplerinin zihinsel çalışmaya uyarlanması. Kısa süreli yoğun odaklanma, dopamin salınımını artırır ve motivasyonu yüksek tutar.',
    benefits: [
      'Hızlı başlangıç ve yüksek motivasyon',
      'Dopamin seviyesini %80 artırır',
      'Kısa vadeli hedeflere odaklanma',
      'Enerji patlaması ve momentum',
      'Erteleme davranışını kırma'
    ],
    effectiveSubjects: [
      'Hızlı problem çözme',
      'E-mail ve iletişim yönetimi',
      'Brainstorming ve fikir üretme',
      'Kısa ödevler ve alıştırmalar',
      'Gözden geçirme ve tekrar',
      'Acil görevler'
    ],
    researchFindings: 'University of California Berkeley\'in 2019 araştırmasında, Sprint tekniği kullanan öğrencilerin görev tamamlama hızı %55 artmış ve motivasyon seviyeleri sürekli yüksek kalmıştır.'
  },
  {
    id: 'microlearning',
    name: 'Micro-Learning',
    description: '15 dakika odaklanma, 5 dakika mola',
    workDuration: 15 * 60,
    breakDuration: 5 * 60,
    color: 'bg-pink-500',
    icon: '🧠',
    scientificBasis: 'Spaced repetition ve mikroöğrenme teorilerine dayanır. Hermann Ebbinghaus\'un unutma eğrisi araştırmaları, kısa aralıklarla tekrar edilen bilgilerin uzun vadeli hafızada %90 daha iyi korunduğunu gösterir.',
    benefits: [
      'Uzun vadeli hafıza oluşumu %90 artış',
      'Kolay sindirilebilir bilgi parçaları',
      'Düzenli tekrar ve pekiştirme',
      'Esnek öğrenme imkanı',
      'Bilgi transferini kolaylaştırır'
    ],
    effectiveSubjects: [
      'Dil öğrenme ve kelime dağarcığı',
      'Formül ve kural ezberlemek',
      'Tarih tarihleri ve olaylar',
      'Coğrafya bilgileri',
      'Tıp terminolojisi',
      'Hızlı gözden geçirme seansları'
    ],
    researchFindings: 'Journal of Educational Psychology\'de yayınlanan 2020 meta-analizi, micro-learning yöntemiyle öğrenen öğrencilerin bilgi kalıcılığının %85 daha yüksek olduğunu ve öğrenme hızının %60 arttığını göstermektedir.'
  }
];

const TimeManagement: React.FC = () => {
  const [selectedTechnique, setSelectedTechnique] = useState<TimeTechnique>(TIME_TECHNIQUES[0]);
  const [timeLeft, setTimeLeft] = useState<number>(TIME_TECHNIQUES[0].workDuration);
  const [isActive, setIsActive] = useState<boolean>(false);
  const [isBreak, setIsBreak] = useState<boolean>(false);
  const [currentCycle, setCurrentCycle] = useState<number>(1);
  const [sessions, setSessions] = useState<TimeSession[]>([]);
  const [showStats, setShowStats] = useState<boolean>(false);
  const [showInfo, setShowInfo] = useState<boolean>(false);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [soundEnabled, setSoundEnabled] = useState<boolean>(true);
  
  // Hedefler için state'ler
  const [goals, setGoals] = useState<Goal[]>([]);
  const [showGoals, setShowGoals] = useState<boolean>(false);
  const [showAddGoal, setShowAddGoal] = useState<boolean>(false);
  const [newGoal, setNewGoal] = useState<Partial<Goal>>({
    title: '',
    description: '',
    targetSessions: 1,
    targetMinutes: 60,
    technique: 'pomodoro',
    deadline: '',
    priority: 'medium'
  });
  
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (isActive && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft(time => time - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      handleTimerComplete();
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isActive, timeLeft]);

  const handleTimerComplete = () => {
    setIsActive(false);
    
    if (soundEnabled && audioRef.current) {
      audioRef.current.play().catch(e => console.log('Ses çalınamadı:', e));
    }

    if (!isBreak) {
      // Çalışma süresi bitti, mola zamanı
      const newSession: TimeSession = {
        id: Date.now().toString(),
        technique: selectedTechnique.name,
        duration: selectedTechnique.workDuration,
        completed: true,
        date: new Date().toISOString()
      };
      setSessions(prev => [...prev, newSession]);

      setIsBreak(true);
      
      // Uzun mola kontrolü (Pomodoro için)
      if (selectedTechnique.id === 'pomodoro' && currentCycle % 4 === 0) {
        setTimeLeft(selectedTechnique.longBreakDuration || selectedTechnique.breakDuration);
      } else {
        setTimeLeft(selectedTechnique.breakDuration);
      }
    } else {
      // Mola bitti, yeni çalışma süresi
      setIsBreak(false);
      setTimeLeft(selectedTechnique.workDuration);
      setCurrentCycle(prev => prev + 1);
    }
  };

  const toggleTimer = () => {
    setIsActive(!isActive);
  };

  const resetTimer = () => {
    setIsActive(false);
    setIsBreak(false);
    setTimeLeft(selectedTechnique.workDuration);
    setCurrentCycle(1);
  };

  const changeTechnique = (technique: TimeTechnique) => {
    setSelectedTechnique(technique);
    setTimeLeft(technique.workDuration);
    setIsActive(false);
    setIsBreak(false);
    setCurrentCycle(1);
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getProgress = (): number => {
    const totalTime = isBreak ? selectedTechnique.breakDuration : selectedTechnique.workDuration;
    return ((totalTime - timeLeft) / totalTime) * 100;
  };

  const getTodayStats = () => {
    const today = new Date().toDateString();
    const todaySessions = sessions.filter(session => 
      new Date(session.date).toDateString() === today
    );
    
    const totalMinutes = todaySessions.reduce((acc, session) => 
      acc + (session.duration / 60), 0
    );
    
    return {
      sessions: todaySessions.length,
      minutes: Math.round(totalMinutes),
      techniques: [...new Set(todaySessions.map(s => s.technique))].length
    };
  };

  const stats = getTodayStats();

  // Hedef yönetimi fonksiyonları
  const addGoal = () => {
    if (!newGoal.title || !newGoal.deadline) return;
    
    const goal: Goal = {
      id: Date.now().toString(),
      title: newGoal.title!,
      description: newGoal.description || '',
      targetSessions: newGoal.targetSessions || 1,
      completedSessions: 0,
      targetMinutes: newGoal.targetMinutes || 60,
      completedMinutes: 0,
      technique: newGoal.technique || 'pomodoro',
      deadline: newGoal.deadline!,
      priority: newGoal.priority || 'medium',
      completed: false,
      createdAt: new Date().toISOString()
    };
    
    setGoals(prev => [...prev, goal]);
    setNewGoal({
      title: '',
      description: '',
      targetSessions: 1,
      targetMinutes: 60,
      technique: 'pomodoro',
      deadline: '',
      priority: 'medium'
    });
    setShowAddGoal(false);
  };

  const updateGoalProgress = (goalId: string, sessions: number, minutes: number) => {
    setGoals(prev => prev.map(goal => {
      if (goal.id === goalId) {
        const updatedGoal = {
          ...goal,
          completedSessions: goal.completedSessions + sessions,
          completedMinutes: goal.completedMinutes + minutes
        };
        updatedGoal.completed = updatedGoal.completedSessions >= updatedGoal.targetSessions && 
                                updatedGoal.completedMinutes >= updatedGoal.targetMinutes;
        return updatedGoal;
      }
      return goal;
    }));
  };

  const deleteGoal = (goalId: string) => {
    setGoals(prev => prev.filter(goal => goal.id !== goalId));
  };

  const toggleGoalComplete = (goalId: string) => {
    setGoals(prev => prev.map(goal => 
      goal.id === goalId ? { ...goal, completed: !goal.completed } : goal
    ));
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getGoalProgress = (goal: Goal) => {
    const sessionProgress = (goal.completedSessions / goal.targetSessions) * 100;
    const minuteProgress = (goal.completedMinutes / goal.targetMinutes) * 100;
    return Math.min(sessionProgress, minuteProgress);
  };

  // Hedef güncelleme - timer tamamlandığında çağrılacak
  useEffect(() => {
    if (sessions.length > 0) {
      const lastSession = sessions[sessions.length - 1];
      const matchingGoals = goals.filter(goal => 
        goal.technique === selectedTechnique.id && !goal.completed
      );
      
      matchingGoals.forEach(goal => {
        updateGoalProgress(goal.id, 1, lastSession.duration / 60);
      });
    }
  }, [sessions]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 dark:text-white mb-2">
            Süre Teknikleri
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300">
            Bilimsel zaman yönetimi teknikleriyle verimliliğinizi artırın
          </p>
        </div>

        {/* Technique Selection */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
          {TIME_TECHNIQUES.map((technique) => (
            <div
              key={technique.id}
              onClick={() => changeTechnique(technique)}
              className={`p-3 sm:p-4 rounded-xl cursor-pointer transition-all duration-300 transform hover:scale-105 ${
                selectedTechnique.id === technique.id
                  ? `${technique.color} text-white shadow-lg`
                  : 'bg-white dark:bg-gray-800 text-gray-800 dark:text-white shadow-md hover:shadow-lg'
              }`}
            >
              <div className="flex items-center mb-2">
                <span className="text-xl sm:text-2xl mr-2 sm:mr-3">{technique.icon}</span>
                <h3 className="font-semibold text-base sm:text-lg">{technique.name}</h3>
              </div>
              <p className={`text-xs sm:text-sm ${
                selectedTechnique.id === technique.id 
                  ? 'text-white/90' 
                  : 'text-gray-600 dark:text-gray-300'
              }`}>
                {technique.description}
              </p>
            </div>
          ))}
        </div>

        {/* Main Timer */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8 mb-6 sm:mb-8">
          <div className="text-center">
            {/* Timer Display */}
            <div className="relative mb-6 sm:mb-8">
              <div className="w-48 h-48 sm:w-56 sm:h-56 lg:w-64 lg:h-64 mx-auto relative">
                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                  <circle
                    cx="50"
                    cy="50"
                    r="45"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                    className="text-gray-200 dark:text-gray-700"
                  />
                  <circle
                    cx="50"
                    cy="50"
                    r="45"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                    strokeDasharray={`${2 * Math.PI * 45}`}
                    strokeDashoffset={`${2 * Math.PI * 45 * (1 - getProgress() / 100)}`}
                    className={`${selectedTechnique.color.replace('bg-', 'text-')} transition-all duration-1000`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-2xl sm:text-3xl lg:text-4xl font-mono font-bold text-gray-800 dark:text-white mb-2">
                      {formatTime(timeLeft)}
                    </div>
                    <div className={`text-xs sm:text-sm font-medium ${
                      isBreak ? 'text-green-600' : selectedTechnique.color.replace('bg-', 'text-')
                    }`}>
                      {isBreak ? 'Mola Zamanı' : 'Çalışma Zamanı'}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Current Technique Info */}
            <div className="mb-4 sm:mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-white mb-2">
                {selectedTechnique.name}
              </h2>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300">
                Döngü: {currentCycle} {selectedTechnique.cycles && `/ ${selectedTechnique.cycles}`}
              </p>
            </div>

            {/* Controls */}
            <div className="flex flex-col sm:flex-row justify-center items-center space-y-3 sm:space-y-0 sm:space-x-4 mb-4 sm:mb-6">
              <button
                onClick={toggleTimer}
                className={`${selectedTechnique.color} text-white px-6 sm:px-8 py-2 sm:py-3 rounded-xl font-semibold flex items-center space-x-2 hover:opacity-90 transition-opacity w-full sm:w-auto`}
              >
                {isActive ? <Pause size={18} /> : <Play size={18} />}
                <span>{isActive ? 'Duraklat' : 'Başlat'}</span>
              </button>
              <button
                onClick={resetTimer}
                className="bg-gray-500 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-semibold flex items-center space-x-2 hover:bg-gray-600 transition-colors w-full sm:w-auto"
              >
                <RotateCcw size={18} />
                <span>Sıfırla</span>
              </button>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row justify-center items-center space-y-2 sm:space-y-0 sm:space-x-4">
              <button
                onClick={() => setShowStats(true)}
                className="bg-blue-500 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-blue-600 transition-colors w-full sm:w-auto text-sm sm:text-base"
              >
                <BarChart3 size={16} />
                <span>İstatistikler</span>
              </button>
              <button
                onClick={() => setShowInfo(true)}
                className="bg-green-500 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-green-600 transition-colors w-full sm:w-auto text-sm sm:text-base"
              >
                <Info size={16} />
                <span>Teknik Bilgisi</span>
              </button>
              <button
                onClick={() => setShowSettings(true)}
                className="bg-purple-500 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-purple-600 transition-colors w-full sm:w-auto text-sm sm:text-base"
              >
                <Settings size={16} />
                <span>Ayarlar</span>
              </button>
            </div>
          </div>
        </div>

        {/* Today's Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 text-center">
            <div className="text-2xl sm:text-3xl font-bold text-blue-600 mb-2">{stats.sessions}</div>
            <div className="text-sm sm:text-base text-gray-600 dark:text-gray-300">Tamamlanan Oturum</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 text-center">
            <div className="text-2xl sm:text-3xl font-bold text-green-600 mb-2">{stats.minutes}</div>
            <div className="text-sm sm:text-base text-gray-600 dark:text-gray-300">Dakika Çalışma</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 text-center">
            <div className="text-2xl sm:text-3xl font-bold text-purple-600 mb-2">{stats.techniques}</div>
            <div className="text-sm sm:text-base text-gray-600 dark:text-gray-300">Farklı Teknik</div>
          </div>
        </div>

        {/* Goals Section */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-4 sm:p-6 mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 space-y-3 sm:space-y-0">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-white flex items-center">
              <Target className="mr-2 sm:mr-3 text-indigo-600" size={24} />
              Hedeflerim
            </h2>
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 w-full sm:w-auto">
              <button
                onClick={() => setShowGoals(!showGoals)}
                className="bg-indigo-500 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center justify-center space-x-2 hover:bg-indigo-600 transition-colors text-sm sm:text-base"
              >
                <Target size={16} />
                <span>{showGoals ? 'Gizle' : 'Göster'}</span>
              </button>
              <button
                onClick={() => setShowAddGoal(true)}
                className="bg-green-500 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center justify-center space-x-2 hover:bg-green-600 transition-colors text-sm sm:text-base"
              >
                <Plus size={16} />
                <span>Yeni Hedef</span>
              </button>
            </div>
          </div>

          {showGoals && (
            <div className="space-y-4">
              {goals.length === 0 ? (
                <div className="text-center py-8">
                  <Target className="mx-auto text-gray-400 mb-4" size={48} />
                  <p className="text-gray-500 dark:text-gray-400">
                    Henüz hedef eklenmemiş. İlk hedefinizi ekleyerek başlayın!
                  </p>
                </div>
              ) : (
                goals.map((goal) => (
                  <div key={goal.id} className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className={`font-semibold text-lg ${goal.completed ? 'line-through text-gray-500' : 'text-gray-800 dark:text-white'}`}>
                            {goal.title}
                          </h3>
                          <span className={`px-2 py-1 rounded-full text-xs border ${getPriorityColor(goal.priority)}`}>
                            {goal.priority === 'high' ? 'Yüksek' : goal.priority === 'medium' ? 'Orta' : 'Düşük'}
                          </span>
                          {goal.completed && (
                            <Check className="text-green-500" size={20} />
                          )}
                        </div>
                        {goal.description && (
                          <p className="text-gray-600 dark:text-gray-300 text-sm mb-2">
                            {goal.description}
                          </p>
                        )}
                        <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                          <span className="flex items-center">
                            <Clock size={14} className="mr-1" />
                            {goal.completedSessions}/{goal.targetSessions} oturum
                          </span>
                          <span className="flex items-center">
                            <Calendar size={14} className="mr-1" />
                            {Math.floor(goal.completedMinutes)}/{goal.targetMinutes} dakika
                          </span>
                          <span>
                            Son tarih: {new Date(goal.deadline).toLocaleDateString('tr-TR')}
                          </span>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => toggleGoalComplete(goal.id)}
                          className={`p-2 rounded-lg transition-colors ${
                            goal.completed 
                              ? 'bg-gray-500 text-white hover:bg-gray-600' 
                              : 'bg-green-500 text-white hover:bg-green-600'
                          }`}
                        >
                          <Check size={16} />
                        </button>
                        <button
                          onClick={() => deleteGoal(goal.id)}
                          className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="mb-2">
                      <div className="flex justify-between text-sm text-gray-600 dark:text-gray-300 mb-1">
                        <span>İlerleme</span>
                        <span>{Math.round(getGoalProgress(goal))}%</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                        <div 
                          className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${getGoalProgress(goal)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Stats Modal */}
        {showStats && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full">
              <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-4">
                Günlük İstatistikler
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-300">Toplam Oturum:</span>
                  <span className="font-semibold text-gray-800 dark:text-white">{stats.sessions}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-300">Toplam Süre:</span>
                  <span className="font-semibold text-gray-800 dark:text-white">{stats.minutes} dakika</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-300">Kullanılan Teknik:</span>
                  <span className="font-semibold text-gray-800 dark:text-white">{stats.techniques}</span>
                </div>
              </div>
              <button
                onClick={() => setShowStats(false)}
                className="w-full mt-6 bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 transition-colors"
              >
                Kapat
              </button>
            </div>
          </div>
        )}

        {/* Teknik Bilgisi Modal */}
        {showInfo && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {selectedTechnique.icon} {selectedTechnique.name}
                </h3>
                <button
                  onClick={() => setShowInfo(false)}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-xl"
                >
                  ✕
                </button>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* Sol Kolon */}
                <div className="space-y-6">
                  {/* Temel Bilgiler */}
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                      ⏱️ Süre Detayları
                    </h4>
                    <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-2">
                      <li className="flex justify-between">
                        <span>Çalışma süresi:</span>
                        <span className="font-medium">{Math.floor(selectedTechnique.workDuration / 60)} dakika</span>
                      </li>
                      <li className="flex justify-between">
                        <span>Mola süresi:</span>
                        <span className="font-medium">{Math.floor(selectedTechnique.breakDuration / 60)} dakika</span>
                      </li>
                      {selectedTechnique.longBreakDuration && (
                        <li className="flex justify-between">
                          <span>Uzun mola:</span>
                          <span className="font-medium">{Math.floor(selectedTechnique.longBreakDuration / 60)} dakika</span>
                        </li>
                      )}
                      {selectedTechnique.cycles && (
                        <li className="flex justify-between">
                          <span>Döngü sayısı:</span>
                          <span className="font-medium">{selectedTechnique.cycles}</span>
                        </li>
                      )}
                    </ul>
                  </div>

                  {/* Bilimsel Temel */}
                  {selectedTechnique.scientificBasis && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                      <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-3 flex items-center">
                        🧬 Bilimsel Temel
                      </h4>
                      <p className="text-sm text-blue-800 dark:text-blue-200 leading-relaxed">
                        {selectedTechnique.scientificBasis}
                      </p>
                    </div>
                  )}

                  {/* Araştırma Bulguları */}
                  {selectedTechnique.researchFindings && (
                    <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
                      <h4 className="font-semibold text-green-900 dark:text-green-100 mb-3 flex items-center">
                        📊 Araştırma Bulguları
                      </h4>
                      <p className="text-sm text-green-800 dark:text-green-200 leading-relaxed">
                        {selectedTechnique.researchFindings}
                      </p>
                    </div>
                  )}
                </div>

                {/* Sağ Kolon */}
                <div className="space-y-6">
                  {/* Faydalar */}
                  {selectedTechnique.benefits && (
                    <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
                      <h4 className="font-semibold text-purple-900 dark:text-purple-100 mb-3 flex items-center">
                        ✨ Bilimsel Faydalar
                      </h4>
                      <ul className="space-y-2">
                        {selectedTechnique.benefits.map((benefit, index) => (
                          <li key={index} className="text-sm text-purple-800 dark:text-purple-200 flex items-start">
                            <span className="text-purple-500 mr-2 mt-1">•</span>
                            <span>{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Etkili Olduğu Dersler */}
                  {selectedTechnique.effectiveSubjects && (
                    <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg border border-orange-200 dark:border-orange-800">
                      <h4 className="font-semibold text-orange-900 dark:text-orange-100 mb-3 flex items-center">
                        📚 Etkili Olduğu Ders Türleri
                      </h4>
                      <div className="grid grid-cols-1 gap-2">
                        {selectedTechnique.effectiveSubjects.map((subject, index) => (
                          <div key={index} className="bg-orange-100 dark:bg-orange-800/30 px-3 py-2 rounded-md">
                            <span className="text-sm text-orange-800 dark:text-orange-200 font-medium">
                              {subject}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Alt Bilgi */}
              <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                  Bu bilgiler akademik araştırmalara dayanmaktadır. Kişisel verimlilik farklılık gösterebilir.
                </p>
              </div>
            </div>
          </div>
        )}



        {/* Add Goal Modal */}
        {showAddGoal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full">
              <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-6 flex items-center">
                <Target className="mr-2 text-indigo-600" size={24} />
                Yeni Hedef Ekle
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Hedef Başlığı *
                  </label>
                  <input
                    type="text"
                    value={newGoal.title || ''}
                    onChange={(e) => setNewGoal(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Örn: Matematik sınavına hazırlanmak"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Açıklama
                  </label>
                  <textarea
                    value={newGoal.description || ''}
                    onChange={(e) => setNewGoal(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Hedef hakkında detaylar..."
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Hedef Oturum
                    </label>
                    <input
                      type="number"
                      value={newGoal.targetSessions || 1}
                      onChange={(e) => setNewGoal(prev => ({ ...prev, targetSessions: parseInt(e.target.value) }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      min="1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Hedef Dakika
                    </label>
                    <input
                      type="number"
                      value={newGoal.targetMinutes || 60}
                      onChange={(e) => setNewGoal(prev => ({ ...prev, targetMinutes: parseInt(e.target.value) }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      min="1"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Teknik
                  </label>
                  <select
                    value={newGoal.technique || 'pomodoro'}
                    onChange={(e) => setNewGoal(prev => ({ ...prev, technique: e.target.value as any }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    {TIME_TECHNIQUES.map(technique => (
                      <option key={technique.id} value={technique.id}>
                        {technique.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Son Tarih *
                    </label>
                    <input
                      type="date"
                      value={newGoal.deadline || ''}
                      onChange={(e) => setNewGoal(prev => ({ ...prev, deadline: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Öncelik
                    </label>
                    <select
                      value={newGoal.priority || 'medium'}
                      onChange={(e) => setNewGoal(prev => ({ ...prev, priority: e.target.value as any }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    >
                      <option value="low">Düşük</option>
                      <option value="medium">Orta</option>
                      <option value="high">Yüksek</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => setShowAddGoal(false)}
                  className="flex-1 bg-gray-500 text-white py-2 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  İptal
                </button>
                <button
                  onClick={addGoal}
                  disabled={!newGoal.title || !newGoal.deadline}
                  className="flex-1 bg-indigo-500 text-white py-2 rounded-lg hover:bg-indigo-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Hedef Ekle
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Settings Modal */}
        {showSettings && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full">
              <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-4">
                Ayarlar
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Volume2 size={20} className="text-gray-600 dark:text-gray-300" />
                    <span className="text-gray-600 dark:text-gray-300">Ses Bildirimleri</span>
                  </div>
                  <button
                    onClick={() => setSoundEnabled(!soundEnabled)}
                    className={`w-12 h-6 rounded-full transition-colors ${
                      soundEnabled ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                      soundEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
              </div>
              <button
                onClick={() => setShowSettings(false)}
                className="w-full mt-6 bg-purple-500 text-white py-2 rounded-lg hover:bg-purple-600 transition-colors"
              >
                Kapat
              </button>
            </div>
          </div>
        )}

        {/* Audio Element */}
        <audio
          ref={audioRef}
          preload="auto"
        >
          <source src="/notification.mp3" type="audio/mpeg" />
        </audio>
      </div>
    </div>
  );
};

export default TimeManagement;